import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Siren, MapPin, Clock, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface SirenAlert {
  id: string;
  city: string;
  area: string;
  threat: string;
  timestamp: string;
}

const fetchSirens = async (): Promise<SirenAlert[]> => {
  const { data, error } = await supabase.functions.invoke("fetch-israel-sirens");
  if (error) throw error;
  return data?.alerts ?? [];
};

const threatColor = (threat: string) => {
  const t = threat.toLowerCase();
  if (t.includes("missile") || t.includes("rocket")) return "text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30";
  if (t.includes("uav") || t.includes("drone")) return "text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30";
  return "text-[hsl(var(--alert))] border-[hsl(var(--alert))]/30";
};

const timeAgo = (ts: string) => {
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

const IsraelSirens = () => {
  const { isPremium } = useAuth();

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ["israel-sirens"],
    queryFn: fetchSirens,
    refetchInterval: isPremium ? 30_000 : 120_000,
    staleTime: 15_000,
  });

  const display = isPremium ? alerts : alerts.slice(0, 8);
  const hasRecent = alerts.some((a) => Date.now() - new Date(a.timestamp).getTime() < 3_600_000);

  return (
    <section id="israel-sirens" className="container mx-auto px-4 py-10">
      <Card className={`border-border bg-card ${hasRecent ? "border-[hsl(var(--danger))]/50" : ""}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-base font-mono uppercase tracking-wider text-foreground">
              <Siren className="h-4 w-4 text-[hsl(var(--danger))]" />
              Israel — Red Alert (Sirens)
              {hasRecent && (
                <Badge className="ml-2 bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] text-[9px] animate-pulse">
                  ACTIVE
                </Badge>
              )}
            </CardTitle>
            <span className="text-[10px] text-muted-foreground">
              {alerts.length} alert{alerts.length !== 1 ? "s" : ""} tracked
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-secondary" />
              ))}
            </div>
          ) : display.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <ShieldAlert className="h-8 w-8 mb-2 text-primary" />
              <p className="text-sm font-medium">No active siren alerts</p>
              <p className="text-xs">All clear — no recent alerts detected.</p>
            </div>
          ) : (
            <div className="grid gap-1.5">
              {display.map((a) => {
                const isRecent = Date.now() - new Date(a.timestamp).getTime() < 3_600_000;
                return (
                  <div
                    key={a.id}
                    className={`flex items-center gap-3 rounded-lg border p-2.5 transition-colors ${
                      isRecent
                        ? "border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/5"
                        : "border-border bg-secondary/30"
                    }`}
                  >
                    <Siren className={`h-4 w-4 shrink-0 ${isRecent ? "text-[hsl(var(--danger))] animate-pulse" : "text-muted-foreground"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{a.city}</span>
                        <Badge variant="outline" className={`text-[9px] ${threatColor(a.threat)}`}>
                          {a.threat}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <MapPin className="h-2.5 w-2.5" /> {a.area}
                        </span>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground whitespace-nowrap">
                      <Clock className="h-2.5 w-2.5" /> {timeAgo(a.timestamp)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {!isPremium && alerts.length > 8 && (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              🔒 {alerts.length - 8} more alerts available with Premium
            </p>
          )}

          <p className="mt-3 text-[10px] text-muted-foreground text-center">
            Data sourced from Pikud HaOref (Home Front Command). Delays may occur.
          </p>
        </CardContent>
      </Card>
    </section>
  );
};

export default IsraelSirens;
